import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Download,
  X,
  Undo2,
  History,
  BadgeDollarSign,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { apiClient } from '@/lib/api'
import { SEMANTIC, CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import { toDisplayDate } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useProducts } from '@/hooks/use-api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { RecordCombobox } from '@/components/ui/record-combobox'
import type {
  CurrencyNormalizationReport,
  ImportBatch,
  MassUploadPreview,
  MassUploadResult,
  MassUploadRow,
  MassUploadProductMapping,
} from '@/types'
import { explainError } from '@/lib/explain-error'

type RowFilter = 'all' | 'valid' | 'warning' | 'error'

// Mass-upload amounts are CAD by definition — records are created as CAD
// regardless of the account's currency, so display them as CAD too.
function formatCurrency(n: number | string): string {
  const num = typeof n === 'string' ? parseFloat(n) : n
  if (!Number.isFinite(num)) return '$0'
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(num)
}

function StatusChip({ status }: { status: 'valid' | 'warning' | 'error' }) {
  if (status === 'valid') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
        style={{ backgroundColor: SEMANTIC.success.fill, color: SEMANTIC.success.text }}
      >
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Valid
      </span>
    )
  }
  if (status === 'warning') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
        style={{ backgroundColor: SEMANTIC.warning.fill, color: SEMANTIC.warning.text }}
      >
        <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Warning
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-destructive/10 text-destructive px-1.5 py-0.5 text-[11px] font-medium">
      <XCircle className="h-3 w-3" aria-hidden="true" /> Error
    </span>
  )
}

function downloadCsv(filename: string, rows: Array<Record<string, unknown>>): void {
  const first = rows[0]
  if (!first) return
  const headers = Object.keys(first)
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join(
    '\n'
  )
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function MassUpload() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<MassUploadPreview | null>(null)
  const [filter, setFilter] = useState<RowFilter>('all')

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [acknowledge, setAcknowledge] = useState(false)
  const [skipWarnings, setSkipWarnings] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [result, setResult] = useState<MassUploadResult | null>(null)

  // Local overrides being edited; applied via the "Apply mappings" button.
  const [pendingOverrides, setPendingOverrides] = useState<Record<string, string | null>>({})
  const [applyingMappings, setApplyingMappings] = useState(false)
  const { data: productsData } = useProducts({ limit: 10000, sort_by: 'name', sort_order: 'asc' })
  const allProducts = productsData?.data || []

  // Persistent history of all batches
  const [batches, setBatches] = useState<ImportBatch[]>([])
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [rollbackTarget, setRollbackTarget] = useState<ImportBatch | null>(null)
  const [rollingBack, setRollingBack] = useState(false)

  // "Verify currencies" — the only mechanism that revisits historical uploads
  // to enforce the amounts-are-CAD rule, with a per-record report of changes.
  const [normalizeConfirmOpen, setNormalizeConfirmOpen] = useState(false)
  const [normalizing, setNormalizing] = useState(false)
  const [normalizeReport, setNormalizeReport] = useState<CurrencyNormalizationReport | null>(null)

  const handleNormalizeCurrencies = async () => {
    setNormalizeConfirmOpen(false)
    setNormalizing(true)
    try {
      const report = await apiClient.normalizeMassUploadCurrencies()
      setNormalizeReport(report)
      if (report.changed_opportunities === 0) {
        toast({
          title: 'All currencies verified',
          description: `All ${report.checked_opportunities.toLocaleString()} mass-uploaded opportunities are recorded in CAD. Nothing needed changing.`,
        })
      } else {
        toast({
          title: 'Currencies corrected',
          description: `${report.changed_opportunities} opportunit${report.changed_opportunities === 1 ? 'y' : 'ies'} and ${report.changed_line_items} line item${report.changed_line_items === 1 ? '' : 's'} relabeled to CAD. Full report below.`,
        })
      }
    } catch (e) {
      toast({
        title: 'Currency check failed',
        description: explainError(e, 'The check made no changes.'),
        variant: 'destructive',
      })
    } finally {
      setNormalizing(false)
    }
  }

  const downloadNormalizeReport = () => {
    if (!normalizeReport || normalizeReport.records.length === 0) return
    downloadCsv(
      `mass-upload-currency-report-${new Date().toISOString().slice(0, 10)}.csv`,
      normalizeReport.records.map((r) => ({
        Batch: r.batch_file || r.import_batch_id || '',
        Opportunity: r.name,
        Account: r.account_name || '',
        'Close Date': r.close_date ? r.close_date.slice(0, 10) : '',
        'Old Currency': r.old_currency || '',
        Amount: r.amount ?? '',
        'Reported Before (CAD)': r.reported_before_cad ?? '',
        'Reported After (CAD)': r.reported_after_cad ?? '',
        'Reporting Change (CAD)': r.reported_change_cad ?? '',
        'Rolled-back Record': r.is_deleted ? 'yes' : 'no',
      }))
    )
  }

  const loadBatches = async () => {
    setLoadingBatches(true)
    try {
      const list = await apiClient.listImportBatches()
      setBatches(list)
    } catch (err) {
      // Previously this silently swallowed the error, leaving the user with
      // an empty list and no indication the backend was unreachable. Surface
      // it so they know to retry. This runs on mount only (and after explicit
      // commit/rollback refreshes) so there's no refetch-storm risk.
      toast({
        title: 'Failed to load import history',
        description: explainError(err, 'Please refresh the page to try again.'),
        variant: 'destructive',
      })
    } finally {
      setLoadingBatches(false)
    }
  }

  useEffect(() => {
    loadBatches()
  }, [])

  const handleRollback = async () => {
    if (!rollbackTarget) return
    setRollingBack(true)
    try {
      const res = await apiClient.rollbackImportBatch(rollbackTarget.id)
      toast({
        title: 'Batch rolled back',
        description: `${res.rolled_back_count.toLocaleString()} opportunit${res.rolled_back_count === 1 ? 'y' : 'ies'} removed.`,
      })
      setRollbackTarget(null)
      // If the rolled-back batch is the one currently shown in results, clear it.
      if (result && result.batch_id === res.batch_id) setResult(null)
      await loadBatches()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      toast({
        title: 'Rollback failed',
        description: err.response?.data?.error || (e as Error).message || 'Rollback failed',
        variant: 'destructive',
      })
    } finally {
      setRollingBack(false)
    }
  }

  const handleFileSelect = (selected: File | null) => {
    if (!selected) return
    const lower = selected.name.toLowerCase()
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.csv')) {
      toast({
        title: 'Unsupported file type',
        description: 'Please upload a .xlsx or .csv file.',
        variant: 'destructive',
      })
      return
    }
    if (selected.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Maximum size is 5 MB.',
        variant: 'destructive',
      })
      return
    }
    setFile(selected)
    setPreview(null)
    setResult(null)
  }

  const handleUpload = async () => {
    if (!file) return
    setUploading(true)
    try {
      const report = await apiClient.massUploadPreview(file)
      setPreview(report)
      setAcknowledge(false)
      setFilter('all')
      setPendingOverrides({})
      toast({
        title: 'File parsed',
        description: `${report.summary.total_rows} rows — ${report.summary.valid_rows} valid, ${report.summary.warning_rows} warnings, ${report.summary.error_rows} errors.`,
      })
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      toast({
        title: 'Preview failed',
        description: err.response?.data?.error || (e as Error).message || 'Failed to parse file',
        variant: 'destructive',
      })
    } finally {
      setUploading(false)
    }
  }

  const clearFile = () => {
    setFile(null)
    setPreview(null)
    setAcknowledge(false)
    setResult(null)
    setPendingOverrides({})
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleApplyMappings = async () => {
    if (!preview) return
    if (Object.keys(pendingOverrides).length === 0) return
    setApplyingMappings(true)
    try {
      const updated = await apiClient.applyMassUploadProductMappings(
        preview.preview_token,
        pendingOverrides
      )
      setPreview(updated)
      setPendingOverrides({})
      toast({
        title: 'Mappings applied',
        description: `Re-validated: ${updated.summary.valid_rows} valid, ${updated.summary.warning_rows} warnings, ${updated.summary.error_rows} errors.`,
      })
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      toast({
        title: 'Failed to apply mappings',
        description: err.response?.data?.error || (e as Error).message || 'Failed',
        variant: 'destructive',
      })
    } finally {
      setApplyingMappings(false)
    }
  }

  const handleCommit = async () => {
    if (!preview) return
    if (!acknowledge) return
    setCommitting(true)
    try {
      const res = await apiClient.massUploadCommit({
        preview_token: preview.preview_token,
        acknowledge_no_attachments: true,
        skip_warnings: skipWarnings,
      })
      setResult(res)
      setConfirmOpen(false)
      toast({
        title: 'Import complete',
        description: `Created ${res.created_count} opportunities — ${formatCurrency(res.total_amount)}`,
      })
      await loadBatches()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      toast({
        title: 'Import failed',
        description: err.response?.data?.error || (e as Error).message || 'Import failed',
        variant: 'destructive',
      })
    } finally {
      setCommitting(false)
    }
  }

  // Filter the row list
  const filteredRows: MassUploadRow[] = preview
    ? preview.rows.filter((r) => filter === 'all' || r.status === filter)
    : []

  const importableCount = preview
    ? preview.summary.valid_rows + (skipWarnings ? 0 : preview.summary.warning_rows)
    : 0

  return (
    <div className="space-y-6" role="main" aria-label="Mass upload opportunities">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/opportunities')}
          aria-label="Back to opportunities"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Badge
          variant="outline"
          style={{
            backgroundColor: CATEGORICAL.navy.tint,
            color: GRACKLE_COLORS.prussianBlue,
            borderColor: CATEGORICAL.navy.solid,
          }}
        >
          <Upload className="mr-1.5 h-3 w-3" aria-hidden="true" />
          Admin
        </Badge>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mass Upload Opportunities</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Bulk-create Closed Won opportunities from a .xlsx or .csv file. Maximum 2,000 rows per
            upload.
          </p>
        </div>
      </div>

      {/* Result state */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2
                className="h-5 w-5"
                style={{ color: CATEGORICAL.evergreen.solid }}
                aria-hidden="true"
              />
              Import complete
            </CardTitle>
            <CardDescription>Batch ID: {result.batch_id}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-2xl font-bold">{result.created_count.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Skipped</p>
                <p className="text-2xl font-bold">{result.skipped_count.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total value</p>
                <p className="text-2xl font-bold">{formatCurrency(result.total_amount)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <Badge variant="outline" className="mt-1">
                  {result.status}
                </Badge>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  downloadCsv(
                    `import-${result.batch_id}.csv`,
                    result.created_opportunities.map((o) => ({
                      id: o.id,
                      name: o.name,
                      amount: o.amount,
                    }))
                  )
                }
                aria-label="Download created opportunities as CSV"
              >
                <Download className="mr-2 h-4 w-4" aria-hidden="true" /> Download results CSV
              </Button>
              <Button onClick={() => navigate('/opportunities')}>View opportunities</Button>
              <Button
                variant="outline"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => {
                  const match = batches.find((b) => b.id === result.batch_id)
                  setRollbackTarget(
                    match || {
                      id: result.batch_id,
                      batch_type: 'opportunity_mass_upload',
                      file_name: file?.name || '',
                      row_count: result.created_count + result.skipped_count,
                      success_count: result.created_count,
                      error_count: 0,
                      warning_count: 0,
                      total_amount: result.total_amount,
                      status: 'completed',
                      started_at: null,
                      completed_at: null,
                      created_date: new Date().toISOString(),
                      uploaded_by_name: null,
                    }
                  )
                }}
                aria-label={`Rollback batch ${result.batch_id}`}
              >
                <Undo2 className="mr-2 h-4 w-4" aria-hidden="true" /> Rollback this import
              </Button>
              <Button variant="ghost" onClick={clearFile}>
                Start new import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload zone */}
      {!result && (
        <Card>
          <CardHeader>
            <CardTitle>Upload spreadsheet</CardTitle>
            <CardDescription>
              Required columns: Customer, AccountID, Product, Date, Amount, OwnerID, Sales Rep,
              LeadSource. All amounts are recorded in CAD, regardless of the account&apos;s
              currency.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  fileInputRef.current?.click()
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0])
              }}
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
              aria-label="Upload file — click or drop a .xlsx or .csv file here"
            >
              <FileSpreadsheet className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
              {file ? (
                <>
                  <p className="font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(0)} KB
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium">
                    Drop your .xlsx or .csv file here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground">Max 5 MB, 2,000 rows</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null)}
              className="sr-only"
              aria-hidden="true"
            />
            {file && !preview && (
              <div className="mt-4 flex gap-2">
                <Button onClick={handleUpload} disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Parsing…
                    </>
                  ) : (
                    'Parse & validate'
                  )}
                </Button>
                <Button variant="ghost" onClick={clearFile}>
                  <X className="mr-2 h-4 w-4" aria-hidden="true" /> Clear
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview state */}
      {preview && !result && (
        <>
          {/* Product mapping — always render when the file has any products so the
             user can override even confident auto-matches. Unmatched ones appear
             first (sorted by the backend). */}
          {preview.product_mappings && preview.product_mappings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Product mapping</CardTitle>
                <CardDescription>
                  One row per distinct product name in the file. The matcher auto-maps where it can;
                  review unmatched rows and use the dropdown to pick the right CRM product. Mapping
                  a name once updates every row that uses it.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th scope="col" className="text-left py-2 font-medium">
                          Raw product name
                        </th>
                        <th scope="col" className="text-right py-2 font-medium">
                          Rows
                        </th>
                        <th scope="col" className="text-left py-2 font-medium">
                          Match
                        </th>
                        <th scope="col" className="text-left py-2 font-medium">
                          Override
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.product_mappings.map((m: MassUploadProductMapping) => {
                        const pending = pendingOverrides[m.raw_product.toLowerCase()]
                        const currentValue = pending ?? m.matched_product_id ?? ''
                        const selectedProduct = allProducts.find((p) => p.id === currentValue)
                        return (
                          <tr key={m.raw_product} className="border-b hover:bg-muted/30 align-top">
                            <td
                              className="py-2 font-medium truncate max-w-[200px]"
                              title={m.raw_product}
                            >
                              {m.raw_product}
                            </td>
                            <td className="py-2 text-right text-muted-foreground">{m.count}</td>
                            <td className="py-2">
                              {m.source === 'exact' && (
                                <span
                                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
                                  style={{
                                    backgroundColor: SEMANTIC.success.fill,
                                    color: SEMANTIC.success.text,
                                  }}
                                >
                                  <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Exact
                                </span>
                              )}
                              {m.source === 'auto' && (
                                <span
                                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
                                  style={{
                                    backgroundColor: SEMANTIC.success.fill,
                                    color: SEMANTIC.success.text,
                                  }}
                                >
                                  <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Auto (
                                  {Math.round(m.match_score * 100)}%)
                                </span>
                              )}
                              {m.source === 'suggested' && (
                                <span
                                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
                                  style={{
                                    backgroundColor: SEMANTIC.warning.fill,
                                    color: SEMANTIC.warning.text,
                                  }}
                                >
                                  <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Suggested
                                  ({Math.round(m.match_score * 100)}%)
                                </span>
                              )}
                              {m.source === 'override' && (
                                <span
                                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
                                  style={{
                                    backgroundColor: CATEGORICAL.navy.tint,
                                    color: GRACKLE_COLORS.prussianBlue,
                                  }}
                                >
                                  Overridden
                                </span>
                              )}
                              {m.source === 'unmatched' && (
                                <span className="inline-flex items-center gap-1 rounded bg-destructive/10 text-destructive px-1.5 py-0.5 text-[11px] font-medium">
                                  <XCircle className="h-3 w-3" aria-hidden="true" /> Unmatched
                                </span>
                              )}
                              <div
                                className="text-xs text-muted-foreground mt-1 truncate max-w-[260px]"
                                title={m.matched_product_name || ''}
                              >
                                {m.matched_product_name || (
                                  <span className="italic">(choose a product →)</span>
                                )}
                              </div>
                            </td>
                            <td className="py-2 min-w-[280px]">
                              <RecordCombobox
                                id={`map-${m.raw_product}`}
                                records={allProducts}
                                value={currentValue}
                                onValueChange={(v) =>
                                  setPendingOverrides((prev) => ({
                                    ...prev,
                                    [m.raw_product.toLowerCase()]: v || null,
                                  }))
                                }
                                displayField={(p: { name?: string }) => p.name || ''}
                                selectedLabel={selectedProduct?.name}
                                placeholder="Pick a CRM product…"
                                searchPlaceholder="Search products…"
                                emptyMessage="No products found"
                                aria-label={`Override product for ${m.raw_product}`}
                                totalCount={productsData?.total}
                              />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {Object.keys(pendingOverrides).length > 0 && (
                  <div
                    className="mt-4 flex items-center justify-between gap-4 rounded-md border p-3"
                    style={{
                      borderColor: CATEGORICAL.clay.solid,
                      backgroundColor: CATEGORICAL.clay.tint,
                    }}
                  >
                    <p className="text-sm">
                      {Object.keys(pendingOverrides).length} pending mapping change
                      {Object.keys(pendingOverrides).length === 1 ? '' : 's'}. Apply them to
                      re-validate the rows.
                    </p>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingOverrides({})}
                        disabled={applyingMappings}
                      >
                        Discard
                      </Button>
                      <Button size="sm" onClick={handleApplyMappings} disabled={applyingMappings}>
                        {applyingMappings ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />{' '}
                            Applying…
                          </>
                        ) : (
                          'Apply mappings & re-check'
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Validation report</CardTitle>
              <CardDescription>File: {preview.file_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Total rows</p>
                  <p className="text-xl font-bold">{preview.summary.total_rows.toLocaleString()}</p>
                </div>
                <div
                  className="rounded-md border p-3"
                  style={{ backgroundColor: CATEGORICAL.evergreen.tint }}
                >
                  <p className="text-xs" style={{ color: GRACKLE_COLORS.prussianBlue }}>
                    Valid
                  </p>
                  <p className="text-xl font-bold" style={{ color: GRACKLE_COLORS.prussianBlue }}>
                    {preview.summary.valid_rows.toLocaleString()}
                  </p>
                </div>
                <div
                  className="rounded-md border p-3"
                  style={{ backgroundColor: CATEGORICAL.clay.tint }}
                >
                  <p className="text-xs" style={{ color: GRACKLE_COLORS.prussianBlue }}>
                    Warnings
                  </p>
                  <p className="text-xl font-bold" style={{ color: GRACKLE_COLORS.prussianBlue }}>
                    {preview.summary.warning_rows.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-md border p-3 bg-destructive/5">
                  <p className="text-xs text-destructive">Errors</p>
                  <p className="text-xl font-bold text-destructive">
                    {preview.summary.error_rows.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Total value</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(preview.summary.total_amount)}
                  </p>
                </div>
              </div>

              {/* By owner / by lead source breakdowns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-md border p-3">
                  <p className="text-sm font-semibold mb-2">By owner</p>
                  <ul className="space-y-1 text-sm">
                    {preview.summary.by_owner.slice(0, 10).map((o) => (
                      <li key={o.owner_id} className="flex justify-between gap-2">
                        <span className="truncate">{o.owner_name || o.owner_id}</span>
                        <span className="text-muted-foreground shrink-0">
                          {o.count} · {formatCurrency(o.amount)}
                        </span>
                      </li>
                    ))}
                    {preview.summary.by_owner.length === 0 && (
                      <li className="text-muted-foreground">—</li>
                    )}
                  </ul>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-sm font-semibold mb-2">By lead source</p>
                  <ul className="space-y-1 text-sm">
                    {preview.summary.by_lead_source.slice(0, 10).map((l) => (
                      <li key={l.lead_source} className="flex justify-between gap-2">
                        <span className="truncate">{l.lead_source}</span>
                        <span className="text-muted-foreground shrink-0">{l.count}</span>
                      </li>
                    ))}
                    {preview.summary.by_lead_source.length === 0 && (
                      <li className="text-muted-foreground">—</li>
                    )}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Row-level table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Rows</CardTitle>
                <CardDescription>Expand a row to see validation details.</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div
                  role="group"
                  aria-label="Filter rows"
                  className="inline-flex rounded-md border"
                >
                  {(['all', 'valid', 'warning', 'error'] as RowFilter[]).map((f) => (
                    <Button
                      key={f}
                      variant={filter === f ? 'default' : 'ghost'}
                      size="sm"
                      aria-pressed={filter === f}
                      onClick={() => setFilter(f)}
                      className="rounded-none first:rounded-l-md last:rounded-r-md"
                    >
                      {f === 'all'
                        ? 'All'
                        : f === 'valid'
                          ? 'Valid'
                          : f === 'warning'
                            ? 'Warnings'
                            : 'Errors'}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    downloadCsv(
                      `validation-${preview.preview_token.slice(0, 8)}.csv`,
                      preview.rows.map((r) => ({
                        row: r.row_number,
                        status: r.status,
                        customer: r.data.customer,
                        account_id: r.data.account_id,
                        product: r.data.product,
                        date: r.data.date,
                        amount: r.data.amount,
                        owner_id: r.data.owner_id,
                        sales_rep: r.data.sales_rep,
                        lead_source: r.data.lead_source,
                        errors: r.errors.join('; '),
                        warnings: r.warnings.join('; '),
                      }))
                    )
                  }
                  aria-label="Download validation report as CSV"
                >
                  <Download className="mr-2 h-3.5 w-3.5" aria-hidden="true" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th scope="col" className="text-left py-2 font-medium">
                        Row
                      </th>
                      <th scope="col" className="text-left py-2 font-medium">
                        Customer
                      </th>
                      <th scope="col" className="text-left py-2 font-medium">
                        Product
                      </th>
                      <th scope="col" className="text-left py-2 font-medium">
                        Date
                      </th>
                      <th scope="col" className="text-right py-2 font-medium">
                        Amount
                      </th>
                      <th scope="col" className="text-left py-2 font-medium">
                        Owner
                      </th>
                      <th scope="col" className="text-left py-2 font-medium">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.slice(0, 500).map((r) => (
                      <tr key={r.row_number} className="border-b hover:bg-muted/30">
                        <td className="py-2 text-muted-foreground">{r.row_number}</td>
                        <td className="py-2">{r.data.customer || '—'}</td>
                        <td className="py-2">{r.data.product || '—'}</td>
                        <td className="py-2">
                          {r.resolved.close_date || (r.data.date ? String(r.data.date) : '—')}
                        </td>
                        <td className="py-2 text-right">
                          {r.resolved.amount
                            ? formatCurrency(r.resolved.amount)
                            : (r.data.amount ?? '—')}
                        </td>
                        <td className="py-2">{r.resolved.owner_name || r.data.sales_rep || '—'}</td>
                        <td className="py-2">
                          <StatusChip status={r.status} />
                          {(r.errors.length > 0 || r.warnings.length > 0) && (
                            <ul className="mt-1 space-y-0.5 text-[11px]">
                              {r.errors.map((msg, i) => (
                                <li key={`e${i}`} className="text-destructive">
                                  {msg}
                                </li>
                              ))}
                              {r.warnings.map((msg, i) => (
                                <li key={`w${i}`} style={{ color: CATEGORICAL.clay.solid }}>
                                  {msg}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredRows.length > 500 && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Showing first 500 of {filteredRows.length.toLocaleString()} rows. Download CSV
                    for the full report.
                  </p>
                )}
                {filteredRows.length === 0 && (
                  <p className="mt-3 text-sm text-muted-foreground text-center">
                    No rows match this filter.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Commit area */}
          <Card>
            <CardHeader>
              <CardTitle>Commit import</CardTitle>
              <CardDescription>
                Creates {importableCount.toLocaleString()} Closed Won opportunit
                {importableCount === 1 ? 'y' : 'ies'}. Error rows are always skipped.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-2">
                <input
                  id="skip-warnings"
                  type="checkbox"
                  checked={skipWarnings}
                  onChange={(e) => setSkipWarnings(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  aria-describedby="skip-warnings-help"
                />
                <Label htmlFor="skip-warnings" className="text-sm font-normal cursor-pointer">
                  Skip rows with warnings ({preview.summary.warning_rows} rows)
                  <span id="skip-warnings-help" className="block text-xs text-muted-foreground">
                    Warning rows are still importable — e.g. customer name doesn't exactly match
                    account name.
                  </span>
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <input
                  id="ack"
                  type="checkbox"
                  checked={acknowledge}
                  onChange={(e) => setAcknowledge(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  aria-required="true"
                  aria-describedby="ack-help"
                />
                <Label htmlFor="ack" className="text-sm font-normal cursor-pointer">
                  I understand these opportunities will be created without file attachments.
                  <span id="ack-help" className="block text-xs text-muted-foreground">
                    Closed Won status normally requires an attached file. Mass-uploaded
                    opportunities are tagged with source='mass_upload' for audit so this bypass is
                    traceable.
                  </span>
                </Label>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={!acknowledge || importableCount === 0}
                >
                  Import {importableCount.toLocaleString()} row{importableCount === 1 ? '' : 's'}
                </Button>
                <Button variant="ghost" onClick={clearFile}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Recent imports — persistent history so users can see everything that's happened */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" aria-hidden="true" /> Recent imports
            </CardTitle>
            <CardDescription>
              Every mass-upload batch, most recent first. Use Rollback to undo a completed batch.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNormalizeConfirmOpen(true)}
              disabled={normalizing}
              aria-label="Verify all previous uploads are recorded in CAD"
            >
              {normalizing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <BadgeDollarSign className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Verify currencies
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadBatches}
              disabled={loadingBatches}
              aria-label="Refresh recent imports"
            >
              {loadingBatches ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                'Refresh'
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No imports yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th scope="col" className="text-left py-2 font-medium">
                      Date
                    </th>
                    <th scope="col" className="text-left py-2 font-medium">
                      File
                    </th>
                    <th scope="col" className="text-left py-2 font-medium">
                      Uploaded by
                    </th>
                    <th scope="col" className="text-right py-2 font-medium">
                      Rows
                    </th>
                    <th scope="col" className="text-right py-2 font-medium">
                      Created
                    </th>
                    <th scope="col" className="text-right py-2 font-medium">
                      Total
                    </th>
                    <th scope="col" className="text-left py-2 font-medium">
                      Status
                    </th>
                    <th scope="col" className="text-right py-2 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} className="border-b hover:bg-muted/30">
                      <td className="py-2 whitespace-nowrap">
                        {(
                          toDisplayDate(b.created_date) ?? new Date(b.created_date)
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="py-2 truncate max-w-[220px]" title={b.file_name}>
                        {b.file_name}
                      </td>
                      <td className="py-2 whitespace-nowrap">{b.uploaded_by_name || '—'}</td>
                      <td className="py-2 text-right">{b.row_count.toLocaleString()}</td>
                      <td className="py-2 text-right">{b.success_count.toLocaleString()}</td>
                      <td className="py-2 text-right">{formatCurrency(b.total_amount)}</td>
                      <td className="py-2">
                        {b.status === 'completed' && (
                          <Badge
                            variant="outline"
                            style={{
                              backgroundColor: SEMANTIC.success.fill,
                              color: SEMANTIC.success.text,
                              borderColor: CATEGORICAL.evergreen.solid,
                            }}
                          >
                            completed
                          </Badge>
                        )}
                        {b.status === 'rolled_back' && (
                          <Badge variant="outline" className="bg-muted text-muted-foreground">
                            rolled back
                          </Badge>
                        )}
                        {b.status === 'processing' && (
                          <Badge
                            variant="outline"
                            style={{
                              backgroundColor: SEMANTIC.warning.fill,
                              color: SEMANTIC.warning.text,
                              borderColor: CATEGORICAL.clay.solid,
                            }}
                          >
                            processing
                          </Badge>
                        )}
                        {b.status === 'failed' && (
                          <Badge
                            variant="outline"
                            className="bg-destructive/10 text-destructive border-destructive/20"
                          >
                            failed
                          </Badge>
                        )}
                        {b.status === 'pending' && <Badge variant="outline">pending</Badge>}
                      </td>
                      <td className="py-2 text-right">
                        {b.status === 'completed' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => setRollbackTarget(b)}
                            aria-label={`Rollback batch ${b.id}`}
                          >
                            <Undo2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Rollback
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Currency verification report */}
      {normalizeReport && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BadgeDollarSign className="h-5 w-5" aria-hidden="true" /> Currency verification
                report
              </CardTitle>
              <CardDescription>
                Checked {normalizeReport.checked_opportunities.toLocaleString()} mass-uploaded
                opportunities.{' '}
                {normalizeReport.changed_opportunities === 0
                  ? 'Everything was already recorded in CAD — nothing changed.'
                  : `${normalizeReport.changed_opportunities} opportunit${normalizeReport.changed_opportunities === 1 ? 'y' : 'ies'} and ${normalizeReport.changed_line_items} line item${normalizeReport.changed_line_items === 1 ? '' : 's'} were relabeled to CAD. Net change to reported revenue: ${formatCurrency(normalizeReport.net_reported_change_cad)}.`}
              </CardDescription>
            </div>
            {normalizeReport.records.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={downloadNormalizeReport}
                aria-label="Download the currency verification report as CSV"
              >
                <Download className="mr-2 h-4 w-4" aria-hidden="true" /> Download CSV
              </Button>
            )}
          </CardHeader>
          {normalizeReport.records.length > 0 && (
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    Mass-uploaded opportunities whose currency was corrected to CAD
                  </caption>
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2 pr-3">Batch</th>
                      <th className="py-2 pr-3">Opportunity</th>
                      <th className="py-2 pr-3">Account</th>
                      <th className="py-2 pr-3">Was</th>
                      <th className="py-2 pr-3 text-right">Amount</th>
                      <th className="py-2 pr-3 text-right">Reported before</th>
                      <th className="py-2 pr-3 text-right">Reported now</th>
                      <th className="py-2 text-right">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {normalizeReport.records.map((r) => (
                      <tr key={r.id} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 whitespace-nowrap">
                          {r.batch_file || r.import_batch_id}
                        </td>
                        <td className="py-1.5 pr-3">
                          {r.name}
                          {r.is_deleted && (
                            <Badge
                              variant="outline"
                              className="ml-2 bg-muted text-muted-foreground"
                            >
                              rolled back
                            </Badge>
                          )}
                        </td>
                        <td className="py-1.5 pr-3">{r.account_name || '—'}</td>
                        <td className="py-1.5 pr-3">{r.old_currency || '—'}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {r.amount != null ? formatCurrency(r.amount) : '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {r.reported_before_cad != null
                            ? formatCurrency(r.reported_before_cad)
                            : '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {r.reported_after_cad != null
                            ? formatCurrency(r.reported_after_cad)
                            : '—'}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          {r.reported_change_cad != null
                            ? formatCurrency(r.reported_change_cad)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Currency verification confirmation */}
      <Dialog open={normalizeConfirmOpen} onOpenChange={setNormalizeConfirmOpen}>
        <DialogContent aria-describedby="normalize-desc">
          <DialogHeader>
            <DialogTitle>Verify upload currencies?</DialogTitle>
            <DialogDescription id="normalize-desc">
              This checks every past mass upload and converts any record not recorded in CAD to CAD
              (upload amounts are CAD by definition). You&apos;ll get a detailed report of every
              record that changed, including the effect on reported revenue. Records already in CAD
              are untouched, and running it again later is safe.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNormalizeConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleNormalizeCurrencies}>Verify and fix</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback confirmation */}
      <Dialog
        open={!!rollbackTarget}
        onOpenChange={(o) => {
          if (!o) setRollbackTarget(null)
        }}
      >
        <DialogContent aria-describedby="rollback-desc">
          <DialogHeader>
            <DialogTitle>Rollback import?</DialogTitle>
            <DialogDescription id="rollback-desc">
              This will soft-delete every opportunity created by batch{' '}
              <code className="font-mono text-xs">{rollbackTarget?.id}</code>
              {rollbackTarget
                ? ` (${rollbackTarget.success_count.toLocaleString()} opportunit${rollbackTarget.success_count === 1 ? 'y' : 'ies'}, ${formatCurrency(rollbackTarget.total_amount)}). `
                : '. '}
              Records are soft-deleted, so nothing is permanently lost. The batch will be marked
              "rolled back".
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRollbackTarget(null)}
              disabled={rollingBack}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="text-destructive hover:bg-destructive/10"
              onClick={handleRollback}
              disabled={rollingBack}
            >
              {rollingBack ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Rolling back…
                </>
              ) : (
                <>
                  <Undo2 className="mr-2 h-4 w-4" aria-hidden="true" /> Rollback
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent aria-describedby="commit-confirm-desc">
          <DialogHeader>
            <DialogTitle>Confirm mass import</DialogTitle>
            <DialogDescription id="commit-confirm-desc">
              About to create {importableCount.toLocaleString()} opportunities marked as Closed Won,
              totalling {formatCurrency(preview?.summary.total_amount || 0)}. This cannot be
              automatically undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={committing}>
              Cancel
            </Button>
            <Button onClick={handleCommit} disabled={committing}>
              {committing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Importing…
                </>
              ) : (
                'Confirm import'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
